import { useState } from 'react';
import { getImagePath } from '@/utils/images';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import WebinarHtml from './WebinarHtml';
import { Container, SectionHead } from './WebinarShared';
import { PlayCircleIcon, LockIcon } from './WebinarIcons';

// Accepts a full YouTube URL (watch?v=, youtu.be/, /embed/) or a bare id and
// returns the privacy-friendly embed URL.
const toEmbedUrl = (url) => {
  if (!url) return '';
  const patterns = [/[?&]v=([^&]+)/, /youtu\.be\/([^?&]+)/, /\/embed\/([^?&]+)/];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return `https://www.youtube-nocookie.com/embed/${m[1]}?autoplay=1`;
  }
  return `https://www.youtube-nocookie.com/embed/${url}?autoplay=1`;
};

const CoursePart = ({ part }) => {
  const [open, setOpen] = useState(false);
  const hasVideo = Boolean(part.youtubeUrl && part.youtubeUrl.trim());
  const topics = (part.topics || '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

  return (
    <div
      className={`group relative flex flex-col gap-4 rounded-2xl border bg-[#2A2422] p-6 transition duration-200 ease-in-out ${
        hasVideo ? 'border-brand-400/40 hover:-translate-y-1 hover:bg-[#312926]' : 'border-white/[0.08]'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className={`font-poly text-[40px] leading-none ${hasVideo ? 'text-brand-400' : 'text-white/[0.16]'}`}>
          {part.partNumber}
        </div>
        {part.dateLabel && (
          <div className="rounded-full border border-white/[0.16] px-3 py-[5px] text-xs font-medium whitespace-nowrap text-white/55">
            {part.dateLabel}
          </div>
        )}
      </div>

      {part.thumbnail?.url && (
        <button
          type="button"
          onClick={() => hasVideo && setOpen(true)}
          disabled={!hasVideo}
          className="relative block aspect-video w-full overflow-hidden rounded-[10px] bg-black"
          aria-label={hasVideo ? `Přehrát: ${part.title || ''}` : undefined}
        >
          <img
            src={getImagePath(part.thumbnail)}
            alt={part.title || ''}
            className={`h-full w-full object-cover ${hasVideo ? '' : 'brightness-[.55] grayscale'}`}
          />
          {hasVideo && (
            <span className="absolute inset-0 flex items-center justify-center text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)]">
              <PlayCircleIcon width={72} height={72} />
            </span>
          )}
        </button>
      )}

      {part.title && <h3 className="font-sans text-[19px] leading-[1.3] font-semibold text-white">{part.title}</h3>}
      {part.subtitle && (
        <div className={`-mt-2 text-[15px] font-medium ${hasVideo ? 'text-brand-500' : 'text-white/40'}`}>
          {part.subtitle}
        </div>
      )}
      {part.description && (
        <WebinarHtml
          html={part.description}
          className="flex flex-col gap-2.5 text-sm leading-[1.55] text-white/[0.62] [&_.q]:text-[15px] [&_.q]:leading-[1.5] [&_.q]:font-semibold [&_.q]:text-white/85"
        />
      )}

      {topics.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t border-white/[0.08] pt-3.5">
          {topics.map((topic) => (
            <span key={topic} className="rounded-full bg-white/[0.06] px-2.5 py-[5px] text-xs text-white/70">
              {topic}
            </span>
          ))}
        </div>
      )}

      <div className="mt-auto flex justify-end pt-1">
        {hasVideo ? (
          <>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="bg-brand-400 hover:bg-brand-500 inline-flex items-center gap-2 rounded-full px-[18px] py-[9px] text-[13px] text-white transition-colors"
            >
              <PlayCircleIcon width={16} height={16} />
              {part.ctaLabel || 'Sledovat díl'}
            </button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogContent className="w-[92vw] max-w-5xl overflow-hidden p-3 sm:p-4">
                <div className="mb-3 flex items-center pr-9">
                  <DialogTitle className="truncate text-sm font-semibold text-black">
                    {part.title || 'Video'}
                  </DialogTitle>
                </div>
                <div className="aspect-video w-full overflow-hidden rounded-xl bg-black">
                  {open && (
                    <iframe
                      src={toEmbedUrl(part.youtubeUrl)}
                      title={part.title || 'YouTube video'}
                      className="h-full w-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </>
        ) : (
          <span className="inline-flex cursor-not-allowed items-center gap-2 rounded-full bg-[#e7e7e7] px-[18px] py-[9px] text-[13px] text-[#949494]">
            <LockIcon width={14} height={14} />
            {part.lockedLabel || 'Již brzy'}
          </span>
        )}
      </div>
    </div>
  );
};

const WebinarCurriculum = ({ eyebrow, heading, intro, parts }) => {
  return (
    <section id="dily" className="bg-grey-950 py-16 md:py-[88px]">
      <Container>
        <SectionHead eyebrow={eyebrow} heading={heading} intro={intro} dark />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {parts?.map((part) => (
            <CoursePart key={part.id} part={part} />
          ))}
        </div>
      </Container>
    </section>
  );
};

export default WebinarCurriculum;
